import { ReactNode, Component, PureComponent, CSSProperties, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { ExcelOptions } from './TimeSeriesStructs';
import { getToolbarHeight } from './ChartHelpers';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { ToolbarButton } from '../../../Tables/ReactTable';
import { handleExcelDownloadClick, tooltipLabelCallback } from "../../../Chart/ChartJsUtils";
import { ChartPortData, ChartsJsonSchemeValidator, ChartDateRange, chartDateRangeValidator } from './ChartsJsonScheme';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { DateFormat, Formatters } from '@Utils/Formats';
import { getRandomSmallKey, isDefined, isFunction, IsNullOrEmpty } from '@Utils/Micro';
import { SettingsList } from '@Primitive/SettingsList';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import RsDateRangePicker from '@Primitive/RsDateRangePicker';
import { eIconType, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { ToolbarIconInfo, ToolbarIconInfo_ButtonRender, ToolbarIconInfo_GetSettingList } from '@Primitive/ToolbarIconButton';
import { WebNode } from '@API/WebNode';
import { Plegma } from '@API/Api';
import { WebThing, WebThingPort } from '@API/WebThing';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
// ------------------------------------------------------------------------------------------------------
const enum PieChartKeys { onToolbarIconClick = 'onToolbarIconClick' };
// ------------------------------------------------------------------------------------------------------
const ChartJsPie = lazy(() => import("./Exports/Pie"));
// =======================================================================================================
export interface PieChartState {
    Data: ChartPortData;
    DateRange: ChartDateRange;
}
export interface PieChartData {
    // Daterange
    showDateRange: boolean;
    initialDateRangeDays: number;
    timePicker: boolean;
    timePickerIncrement: number;
    timePickerInitialRange: number;

    // Legend
    showLegend: boolean;
    legendPosition: 'left' | 'right' | 'top' | 'bottom';
    legendUsePointStyle: boolean;
    legendFontColor: string;
    legendFontSize: number;
    multilineLegened?: boolean;
    legendColumnsPerRow?: number;
    legendColumnsAutospan?: boolean;

    dataPrefix: string;
    dataPostfix: string;

    /**
     * Allows to download an excel spreadsheet (XLSX file) 
     * with the extracted data from the chart.
     */
    excelDownload: boolean;
    excelOptions: ExcelOptions;

    //Toolbar
    customToolbarIcons: ToolbarIconInfo[];
    defaultIconsColor?: string;
    toolbarIconSize?: number;
    toolbarHeight?: number;
}
// ------------------------------------------------------------------------------------------------------
class PieChart extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<PieChartData>, PieChartState> {
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<PieChartData>) {
        super(props);

        let data: ChartPortData = null;
        if (_.has(this.props.WidgetDefaultValues, PortKeys.Data)) {
            data = _.cloneDeep(this.props.WidgetDefaultValues[PortKeys.Data]);
        }

        this.state = {
            Data: data,
            DateRange: {
                StartDate: null,
                EndDate: null
            }
        };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.UpdateData(this.props);
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<any>) {
        if (this.props.PortVersion != prevProps.PortVersion)
            this.UpdateData(this.props);
    }
    // --------------------------------------------------------------------------------------------
    get iconToolbarIconsProps(): CSSProperties {
        return {
            padding: 0,
            margin: '0 4px',
            width: this.props.WidgetData.toolbarHeight,
            height: this.props.WidgetData.toolbarHeight,
            lineHeight: this.props.WidgetData.toolbarHeight + "px",
            fontSize: this.props.WidgetData.toolbarIconSize,
            cursor: "pointer"
        };
    }
    // ---------------------------------------------------------------------------------------------------
    onToolbarIconClick = (iconInfo: ToolbarIconInfo) => this.props.SendPortEvent(PieChartKeys.onToolbarIconClick, iconInfo.id);
    // ---------------------------------------------------------------------------------------------------
    UpdateData(props: WidgetsThingsStructs.PartialIWidgetThingsProps<PieChartData>) {
        if (_.has(props.PortsStates, PortKeys.Data)) {
            let port = props.PortsStates[PortKeys.Data];
            if (port.State != null && port.State != "") {
                if (port.LastStateStatus == StateStatus.NoData) {
                    if (_.has(props.WidgetDefaultValues, PortKeys.Data))
                        this.setState({ Data: _.cloneDeep(props.WidgetDefaultValues[PortKeys.Data]) });
                    else
                        this.setState({ Data: null });
                } else if (port.LastStateStatus == StateStatus.Valid) {
                    this.setState({ Data: _.cloneDeep(port.State) });
                } else {
                    this.setState({ Data: null });
                }
            }
        }

        let hasDateRangePortState = false;
        if (_.has(props.PortsStates, PortKeys.DateRange)) {
            let dateRangePort = props.PortsStates[PortKeys.DateRange];

            // is it touched?
            if (!IsNullOrEmpty(dateRangePort.State)) {
                hasDateRangePortState = true;
                let dateRange: ChartDateRange;

                // set the date range
                switch (dateRangePort.LastStateStatus) {
                    case StateStatus.Valid:
                        dateRange = _.cloneDeep(dateRangePort.State);
                        break;
                    case StateStatus.NoData:
                        dateRange = _.cloneDeep(props.WidgetDefaultValues[PortKeys.DateRange]);
                        break;
                    default:
                        break;
                }
                this.setState({ DateRange: dateRange });
            }
        }

        // if no data then try to get the default date range
        if (!hasDateRangePortState) {
            if (_.has(props.WidgetDefaultValues, PortKeys.DateRange)) {
                let dateRange = _.cloneDeep(props.WidgetDefaultValues[PortKeys.DateRange]);
                this.setState({ DateRange: dateRange as ChartDateRange });
            }
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _sendDaterangePortEvent = (startDay: string, endDay: string) => {
        // Send port event msg for the selected range.
        this.props.SendPortEvent(PortKeys.SelectedRange, JSON.stringify({
            StartDate: startDay,
            EndDate: endDay
        } as ChartDateRange));
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const { Data, DateRange } = this.state;
        const {
            NodeKey,
            ThingKey,
            WidgetData,
            SendPortEvent,
            width: totalWd,
            height: totalHt,
            WidgetDefaultValues
        } = this.props;

        let toolbarButtons = ToolbarButton.none; //todo show be taken from settings
        const showToolbarIcons = (
            isDefined(this.props.WidgetData.customToolbarIcons) &&
            this.props.WidgetData.customToolbarIcons.length > 0
        ) || (toolbarButtons != ToolbarButton.none);

        const pad = WidgetsThingsStructs.defaultWidgetContainerPadding;
        const hideToolbar = !(WidgetData.showDateRange || WidgetData.excelDownload || showToolbarIcons);
        const toolbarHt = hideToolbar ? 0 : getToolbarHeight(totalHt);
        const width = totalWd - 2 * pad;
        const height = totalHt - 2 * pad - toolbarHt;

        let rangeValue = null;
        if (DateRange != null && DateRange.StartDate != null && DateRange.EndDate != null)
            rangeValue = [new Date(DateRange.StartDate), new Date(DateRange.EndDate)];

        return <div style={{ padding: pad }}>
            {/* Toolbar */}
            {hideToolbar ? null :
                <div
                    style={{
                        padding: 0,
                        height: toolbarHt,
                        width: width,
                        backgroundColor: 'transparent',
                        display: "flex",
                        alignContent: "center",
                        justifyContent: "flex-end",
                        alignItems: "center"
                    }}>
                    {WidgetData.showDateRange ?
                        <RsDateRangePicker
                            transparent
                            navEnabled
                            size='sm'
                            usePredefinedRanges
                            cleanable={false}
                            localizationKey={NodeKey}
                            hasTime={WidgetData.timePicker}
                            //timePickerIncrement={WidgetData.timePickerIncrement}
                            initialDaysDiff={WidgetData.initialDateRangeDays}
                            initialHoursDiff={WidgetData.timePickerInitialRange}
                            value={rangeValue}
                            onChange={undefined}
                            onChangeBackwardCompatible={this._sendDaterangePortEvent} />
                        : null}
                    {WidgetData.excelDownload
                        ? <i
                            className={BoardTheming.GetTheme(this.props.NodeKey).widgets.charts.excelIcon}
                            style={{ color: this.props.WidgetData.defaultIconsColor, ...this.iconToolbarIconsProps }}
                            onClick={() => handleExcelDownloadClick(
                                WidgetData.excelOptions.fileName,
                                WebNode
                                    .GetInstanceKey(NodeKey)
                                    .StateManager
                                    .GetState(ThingKey),
                                PortKeys.Data,
                                WidgetDefaultValues
                            )} />
                        : null}
                    {showToolbarIcons ?
                        ToolbarIconInfo_ButtonRender(this.props.WidgetData.customToolbarIcons, [], [], this.iconToolbarIconsProps, this.onToolbarIconClick, 'charts')
                        : null}
                </div>
            }
            {/* Content */}
            {!isDefined(Data)
                ? WidgetsThingsStructs.DefaultMessageOnNoData(width, height)
                : <Suspense fallback={<LazyLoadingAnimation />}>
                    <ChartJsPie
                        id={ThingKey}
                        width={width}
                        height={height}
                        multilineLegend={WidgetData.multilineLegened}
                        legendColumnsPerRow={WidgetData.legendColumnsPerRow}
                        legendColumnsAutospan={WidgetData.legendColumnsAutospan}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            animation: null,
                            legend: {
                                display: WidgetData.showLegend,
                                position: WidgetData.legendPosition,
                                labels: {
                                    fontSize: WidgetData.legendFontSize,
                                    fontColor: WidgetData.legendFontColor,
                                    usePointStyle: WidgetData.legendUsePointStyle
                                }
                            },
                            tooltips: {
                                callbacks: {
                                    label: (tooltipItem, data) => tooltipLabelCallback(
                                        tooltipItem,
                                        data,
                                        WidgetData.dataPrefix,
                                        WidgetData.dataPostfix
                                    )
                                }
                            },
                            onClick: (ev: MouseEvent, elems: Array<{}>) => {
                                const el: any = elems[0];
                                if (!isDefined(el))
                                    return;

                                // Get related indexes.
                                const elIdx = el._index;
                                const dsIdx = el._datasetIndex;

                                if (isDefined(el) &&
                                    isDefined(elIdx) &&
                                    isDefined(dsIdx)) {
                                    // Send event for clicked dataset.
                                    SendPortEvent(
                                        PortKeys.SelectedData,
                                        JSON.stringify({
                                            Label: Data.labels[elIdx],
                                            Dataset: Data.datasets[dsIdx].label,
                                            Value: Data.datasets[dsIdx].data[elIdx]
                                        })
                                    );
                                }
                            }
                        }}
                        data={Data}
                        redraw />
                </Suspense>}
        </div>;
    }
}
// =======================================================================================================
class PieWidgetSettings extends PureComponent<IWidgetSettingsProps<PieChartData>> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<PieChartData>) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<PieChartData>[] = [
            {
                name: "Toolbar",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Toolbar Custom Icons",
                        tooltip: "Extra custom icons in toolbar",
                        dataPath: "customToolbarIcons",
                        itemsInfo: ToolbarIconInfo_GetSettingList(),
                        createNewItem: () => {
                            return {
                                icon: "",
                                tooltip: "Icon tooltip",
                                iconType: eIconType.fontAwesome,
                                id: "Icon " + getRandomSmallKey(),
                                iconColor: "#2c2c2c",
                                nestedItems: []
                            } as ToolbarIconInfo;
                        }
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Height",
                        dataPath: "toolbarHeight",
                        minValue: 20,
                        maxValue: 80,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Icon Size",
                        dataPath: "toolbarIconSize",
                        minValue: 14,
                        maxValue: 24,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Default Icons Color',
                        tooltip: 'Set value for default Icons.',
                        dataPath: "defaultIconsColor",
                    },
                ]
            },
            {
                name: "Date Range",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Date Range',
                        dataPath: "showDateRange",
                        tooltip: 'If enabled, a date range picker will be shown in the toolbar.'
                    },
                    {
                        type: SettingsType.Number,
                        label: "Initial Days Period",
                        dataPath: "initialDateRangeDays",
                        tooltip: 'Specify the initial period of the range in days. Default value is set to 30 days, meaning that when you load the page, the range will span from current day to 30 days before.',
                        minValue: 0,
                        isHidden: (data: PieChartData) => !data.showDateRange,
                        oneLine: true
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Selection of dates with time",
                        tooltip: 'Whether to include time in range selection or not.',
                        dataPath: "timePicker",
                        isHidden: (data: PieChartData) => !data.showDateRange
                    },
                    {
                        type: SettingsType.Number,
                        label: "Time Picker increment (min)",
                        dataPath: "timePickerIncrement",
                        tooltip: 'Specify the step of the time picker in minutes.',
                        maxValue: 60,
                        minValue: 0,
                        isHidden: (data: PieChartData) => !data.showDateRange || !data.timePicker,
                        oneLine: true
                    },
                    {
                        type: SettingsType.Number,
                        label: "Initial Hour Period",
                        dataPath: "timePickerInitialRange",
                        tooltip: 'Specify the initial period of the range in hours.',
                        minValue: 0,
                        isHidden: (data: PieChartData) => !data.showDateRange || !data.timePicker,
                        oneLine: true
                    },

                ]
            },
            {
                name: "Legend",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Legend',
                        dataPath: "showLegend",
                        tooltip: 'If enabled, show the legend'
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Position",
                        dataPath: "legendPosition",
                        tooltip: 'Set the position of the legend (top, bottom, left, right)',
                        items: [
                            { value: 'top', label: 'Top' },
                            { value: 'left', label: 'Left' },
                            { value: 'bottom', label: 'Bottom' },
                            { value: 'right', label: 'Right' }
                        ],
                        isHidden: (data: PieChartData) => !data.showLegend
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Multiple Line Legend',
                        dataPath: "multilineLegened",
                        tooltip: 'If enabled, shows the legend in multiple row only when legend positon is top,bottom.'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Column Autospan',
                        dataPath: "legendColumnsAutospan",
                        tooltip: 'If enabled columns autospan',
                        isHidden: (data: PieChartData) => !data.multilineLegened

                    },
                    {
                        type: SettingsType.Number,
                        label: "Columns Per Row",
                        dataPath: "legendColumnsPerRow",
                        tooltip: 'Specify the number of the columns per row in case of multiline legend.',
                        minValue: 1,
                        disable: this.props.WidgetData.legendColumnsAutospan || this.props.WidgetData.legendPosition == 'left' || this.props.WidgetData.legendPosition == 'right',//mutual exclusive with autospan
                        isHidden: (data: PieChartData) => !data.multilineLegened

                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Use Point Style",
                        tooltip: 'Label style will match corresponding point style',
                        dataPath: "legendUsePointStyle",
                        isHidden: (data: PieChartData) => !data.showLegend
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: "Font Color",
                        tooltip: 'Color of legend text',
                        dataPath: "legendFontColor",
                        isHidden: (data: PieChartData) => !data.showLegend
                    },
                    {
                        type: SettingsType.Number,
                        label: "Font Size",
                        tooltip: 'Font size of legend text (9-25)',
                        dataPath: "legendFontSize",
                        minValue: 9,
                        maxValue: 25,
                        isHidden: (data: PieChartData) => !data.showLegend,
                        oneLine: true
                    }
                ]
            },
            {
                name: 'Tooltip',
                elements: [
                    {
                        type: SettingsType.String,
                        label: 'Data Prefix',
                        tooltip: 'Add tooltip when hovering the chart',
                        dataPath: 'dataPrefix'
                    },
                    {
                        type: SettingsType.String,
                        label: 'Data Postfix',
                        tooltip: 'Add tooltip when hovering the chart',
                        dataPath: 'dataPostfix'
                    }
                ]
            },
            {
                name: "Excel Download",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Excel Download',
                        tooltip: 'Allows to download an excel spreadsheet (XLSX file) with the extracted data from the chart',
                        dataPath: 'excelDownload'
                    },
                    {
                        type: SettingsType.String,
                        label: "File Name",
                        dataPath: "excelOptions.fileName",
                        tooltip: 'Define excel file name',
                        oneLine: true,
                        isHidden: (data: PieChartData) => !data.excelDownload
                    },
                ]
            },
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isFunction(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================

const enum PortKeys {
    Data = "data",
    DateRange = 'daterange',
    SelectedRange = "SelectedRange",
    SelectedData = "SelectedData"
}
export class PieChartWidget extends IWidgetThingsClass<PieChartData> {
    // ------------------------------------------------------------------------------------------------
    Render(): ReactNode { return <PieChart />; }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <PieWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    public initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data)
        ));

        ports.push(new WebThingPort(
            {
                Name: 'Date Range',
                PortKey: PortKeys.DateRange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            data => this.ValidateValues(PortKeys.DateRange, data)
        ));

        ports.push(new WebThingPort(
            {
                Name: "Selected Data",
                PortKey: PortKeys.SelectedData,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            },
            WebThingPortType.Trigger
        ));

        // Calculate the range
        let startDate = moment().startOf('minute').subtract(wData.initialDateRangeDays, 'days')
        if (wData.timePicker)
            startDate.subtract(wData.timePickerInitialRange, "hours");
        let endDate = moment().startOf('minute');
        let dates = Formatters.datetimeRange(startDate, endDate,
            DateFormat.ISO, wData.timePicker, true).split(' - ');


        ports.push(new WebThingPort(
            {
                Name: "Selected Range",
                PortKey: PortKeys.SelectedRange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: JSON.stringify({ StartDate: dates[0], EndDate: dates[1] })
            },
            WebThingPortType.Data
        ));

        ports.push(new WebThingPort({
            Name: 'onToolbarIconClick',
            PortKey: PieChartKeys.onToolbarIconClick,
            Type: Plegma.ePortType.String,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ""
        }, WebThingPortType.Data));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): PieChartData {
        return {
            showDateRange: false,
            timePicker: false,
            timePickerIncrement: 5,
            timePickerInitialRange: 0,
            initialDateRangeDays: 30,

            showLegend: true,
            legendPosition: 'top',
            legendUsePointStyle: false,
            legendFontColor: '#666',
            legendFontSize: 12,
            multilineLegened: false,
            legendColumnsPerRow: 1,
            legendColumnsAutospan: true,

            dataPrefix: '',
            dataPostfix: '',

            excelDownload: false,
            excelOptions: {
                fileName: "Data",
            },
            customToolbarIcons: [],
            toolbarHeight: 25,
            toolbarIconSize: 15
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return PieChartWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: PieChartData = PieChartWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Pie Chart",
            WidgetTitle: "",
            Key: "PieChartWidget",
            Type: "PieChartWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/pie_chart.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultValues(): { [key: string]: any } {
        return {
            [PortKeys.Data]: null
        };
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.Data) {
            return ChartsJsonSchemeValidator().DataValidateString(value);
        } else if (key == PortKeys.DateRange) {
            return chartDateRangeValidator().DataValidateString(value);
        }
        return { valid: true, errors: '' };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    }
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================